import type {
  PresentationContent,
  PresentationExportPreset,
  PresentationLocale,
  PresentationPresetId,
  PresentationSourceData,
  SlideProject,
} from "./types";

const CALENDLY_URL = "https://calendly.com/dimensione4-di-dariomarcobellini/30min";

const SOCIALS = [
  { label: "LinkedIn", href: "https://linkedin.com/in/dariobellini" },
  { label: "GitHub", href: "https://github.com/Dimensione4" },
  { label: "X", href: "https://x.com/Dimensione4it" },
  { label: "Instagram", href: "https://instagram.com/dimensione4.it" },
  { label: "TikTok", href: "https://tiktok.com/@dimensione4.it" },
];

const PROJECT_YEAR_MAP: Record<string, string> = {
  "Digital Twin Platform": "2021-2022",
  "Connected Industrial Worker": "2022-2023",
  "AI Assistant Platform": "2023-2024",
  "IoT Platform": "2024-2025",
  "Digital Payments Platform": "2025-2026",
};

const PRESETS: Record<PresentationPresetId, PresentationExportPreset> = {
  full: {
    id: "full",
    slideIds: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    fileNameByLocale: {
      it: "dimensione4-cv-tecnico-freelance.pdf",
      en: "dimensione4-freelance-technical-cv.pdf",
    },
  },
  experience: {
    id: "experience",
    slideIds: [3, 4, 5, 6],
    fileNameByLocale: {
      it: "dimensione4-vision-mission.pdf",
      en: "dimensione4-vision-mission.pdf",
    },
  },
  presentation: {
    id: "presentation",
    slideIds: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    fileNameByLocale: {
      it: "dimensione4-presentazione-engineering-slide.pdf",
      en: "dimensione4-engineering-presentation-slides.pdf",
    },
  },
};

function withYears(projects: PresentationSourceData["projects"]): SlideProject[] {
  return projects.map((project, index) => ({
    ...project,
    year: PROJECT_YEAR_MAP[project.title] ?? `${2021 + index}-${2022 + index}`,
  }));
}

export function getPresentationPreset(id: PresentationPresetId): PresentationExportPreset {
  return PRESETS[id];
}

export function buildPresentationContent(
  locale: PresentationLocale,
  data: PresentationSourceData
): PresentationContent {
  const isItalian = locale === "it";

  return {
    locale,
    personName: "Dario Marco Bellini",
    roleTitle: "Full Stack Developer",
    heroTitle: data.heroTitle,
    heroSubtitle: data.heroSubtitle,
    trustLine: data.trustLine,
    heroTags: data.heroStack,
    projects: withYears(data.projects),
    techGroups: data.techGroups,
    mindset: data.mindset,
    freelanceFocus: data.freelanceFocus,
    calendlyUrl: CALENDLY_URL,
    email: "dariomarcobellini@dimensione4.it",
    website: "https://dimensione4.it",
    socials: SOCIALS,
    logoSrc: "/src/assets/logo-symbol.png",
    photoSrc: "/about/dario-portrait-16x9.webp",
    labels: isItalian
      ? {
          timelineTitle: "Esperienza Accenture",
          projectTitle: "Progetti Enterprise",
          projectSubtitle: "Digital Twin e contesti industriali",
          innovativeTitle: "Progetti Innovativi",
          innovativeSubtitle: "AI Assistant e IoT Ecosystem",
          fintechTitle: "Progetto FinTech",
          stackTitle: "Stack Tecnologico",
          stackSubtitle: "Frameworks e strumenti reali",
          servicesTitle: "Servizi e metodo",
          servicesSubtitle: "Dall'analisi all'esecuzione",
          missionVisionTitle: "Mission & Vision",
          missionTitle: "Mission",
          visionTitle: "Vision",
          missionText:
            "Progettare e sviluppare prodotti digitali ad alto impatto, combinando precisione ingegneristica, design su misura e tempi di esecuzione rapidi.",
          visionText:
            "Essere il partner tecnico-creativo di riferimento per imprenditori e team che vogliono costruire piattaforme solide, scalabili e distintive.",
          contactTitle: "Parliamo del tuo progetto",
          contactSubtitle:
            "Dal concept alla produzione scalabile. Trasformo idee complesse in soluzioni concrete.",
          problem: "Problema",
          action: "Intervento",
          impact: "Impatto",
          bookCall: "Book a call (30 min)",
          openToProjects: "Open for new projects",
          connect: "Connettiti",
        }
      : {
          timelineTitle: "Accenture Experience",
          projectTitle: "Enterprise Projects",
          projectSubtitle: "Digital Twin and industrial contexts",
          innovativeTitle: "Innovative Projects",
          innovativeSubtitle: "AI Assistant and IoT Ecosystem",
          fintechTitle: "FinTech Project",
          stackTitle: "Tech Stack",
          stackSubtitle: "Real frameworks and tooling",
          servicesTitle: "Services and method",
          servicesSubtitle: "From analysis to execution",
          missionVisionTitle: "Mission & Vision",
          missionTitle: "Mission",
          visionTitle: "Vision",
          missionText:
            "Design and build high-impact digital products by combining engineering rigor, bespoke design, and fast execution.",
          visionText:
            "Be the trusted technical-creative partner for founders and teams building solid, scalable, and distinctive platforms.",
          contactTitle: "Let's talk about your project",
          contactSubtitle:
            "From concept to scalable production. I turn complex ideas into concrete digital solutions.",
          problem: "Problem",
          action: "Action",
          impact: "Impact",
          bookCall: "Book a call (30 min)",
          openToProjects: "Open for new projects",
          connect: "Connect",
        },
  };
}
